import * as v from "valibot";

import Bugsnag from "@/utils/bugsnag";
import {
  CompleteUploadResponseSchema,
  CreateUploadResponseSchema,
  SimpleUploadResponseSchema,
} from "@/utils/schemas";

const EDGE_FUNCTION_URL =
  process.env.EXPO_PUBLIC_SUPABASE_URL + "/functions/v1/r2-upload-url";

interface CompletedPart {
  ETag: string;
  PartNumber: number;
}

interface UploadProgress {
  loaded: number;
  partNumber: number;
  total: number;
  totalParts: number;
}

export async function multipartUpload(
  file: File,
  accessToken: string,
  onProgress?: (progress: UploadProgress) => void,
): Promise<{ key: string; url: string }> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
  };

  // Step 1: Create multipart upload and get presigned URLs
  Bugsnag.leaveBreadcrumb("[r2-upload] starting upload", {
    contentType: file.type,
    fileName: file.name,
    fileSize: String(file.size),
  });

  const createRes = await fetch(EDGE_FUNCTION_URL, {
    body: JSON.stringify({
      action: "create",
      contentType: file.type || "application/octet-stream",
      fileName: file.name,
      fileSize: file.size,
    }),
    headers,
    method: "POST",
  });

  if (!createRes.ok) {
    const text = await createRes.text();
    console.error("[r2-upload] create failed", {
      body: text,
      status: createRes.status,
    });
    throw new Error(`Failed to create upload: ${text}`);
  }

  const createJson = await createRes.json();
  Bugsnag.leaveBreadcrumb("[r2-upload] create response", {
    isSimple: String("presignedUrl" in createJson),
    key: createJson.key,
  });

  // Simple upload path for small files (server returns presignedUrl instead of multipart fields)
  if ("presignedUrl" in createJson) {
    const { key, presignedUrl, url } = v.parse(
      SimpleUploadResponseSchema,
      createJson,
    );

    Bugsnag.leaveBreadcrumb("[r2-upload] simple upload to presigned URL", {
      contentType: file.type,
      fileSize: String(file.size),
      urlDomain: new URL(presignedUrl).hostname,
    });

    let uploadRes: Response;
    try {
      uploadRes = await fetch(presignedUrl, {
        body: file,
        method: "PUT",
      });
    } catch (err) {
      console.error("[r2-upload] PUT fetch failed (likely CORS)", {
        error: err instanceof Error ? err.message : err,
        presignedUrl,
      });
      throw err;
    }

    if (!uploadRes.ok) {
      const text = await uploadRes.text();
      console.error("[r2-upload] PUT returned error", {
        body: text,
        status: uploadRes.status,
      });
      throw new Error(`Upload failed: ${uploadRes.status}`);
    }

    Bugsnag.leaveBreadcrumb("[r2-upload] simple upload succeeded");
    onProgress?.({
      loaded: file.size,
      partNumber: 1,
      total: file.size,
      totalParts: 1,
    });
    return { key, url };
  }

  // Multipart upload path for large files
  const { key, partSize, presignedUrls, uploadId } = v.parse(
    CreateUploadResponseSchema,
    createJson,
  );

  const parts: CompletedPart[] = [];

  try {
    for (let i = 0; i < presignedUrls.length; i++) {
      const start = i * partSize;
      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);

      let uploadRes: Response;
      try {
        uploadRes = await fetch(presignedUrls[i], {
          body: chunk,
          method: "PUT",
        });
      } catch (err) {
        console.error(`[r2-upload] part ${i + 1} fetch failed (likely CORS)`, {
          error: err instanceof Error ? err.message : err,
          url: presignedUrls[i],
        });
        throw err;
      }

      if (!uploadRes.ok) {
        const text = await uploadRes.text();
        console.error(`[r2-upload] part ${i + 1} returned error`, {
          body: text,
          status: uploadRes.status,
        });
        throw new Error(`Part ${i + 1} upload failed: ${uploadRes.status}`);
      }

      const etag = uploadRes.headers.get("ETag");
      if (!etag) throw new Error(`No ETag returned for part ${i + 1}`);

      parts.push({ ETag: etag, PartNumber: i + 1 });

      onProgress?.({
        loaded: end,
        partNumber: i + 1,
        total: file.size,
        totalParts: presignedUrls.length,
      });
    }

    // Step 3: Complete the multipart upload
    const completeRes = await fetch(EDGE_FUNCTION_URL, {
      body: JSON.stringify({
        action: "complete",
        key,
        parts,
        uploadId,
      }),
      headers,
      method: "POST",
    });

    if (!completeRes.ok) {
      throw new Error(`Failed to complete upload: ${await completeRes.text()}`);
    }

    const { key: finalKey, url } = v.parse(
      CompleteUploadResponseSchema,
      await completeRes.json(),
    );
    return { key: finalKey, url };
  } catch (err) {
    // Best-effort abort so R2 cleans up incomplete parts
    await fetch(EDGE_FUNCTION_URL, {
      body: JSON.stringify({ action: "abort", key, uploadId }),
      headers,
      method: "POST",
    }).catch(() => {});

    throw err;
  }
}
